import {useTranslation} from '@aboutyou/i18n';
import {createRouteState, useHistory} from '@aboutyou/router';
import React, {FC, useRef} from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {useIsomorphicLayoutEffect} from '../../../hooks/useIsomorphicLayoutEffect/useIsomorphicLayoutEffect';
import {useShowToast} from '../../atoms/Toast/hooks/useShowToast';
import {UnstyledLink} from '../../atoms/UnstyledLink/UnstyledLink';
import {HorizontalList} from '../../molecules/HorizontalList/HorizontalList';

type Category = {
  active: boolean;
  disabled: boolean;
  id: number;
  label: string;
  target: string;
  overrideTarget?: string;
};

interface Props {
  categories: Array<Category>;
  onClick: () => void;
  useButtonLink?: boolean;
}

export const CategoryList: FC<Props> = ({
  categories,
  onClick,
  useButtonLink,
}) => {
  const {t} = useTranslation();
  const history = useHistory();
  const showToast = useShowToast();

  const {sliderRef, activeCategoryRef} = useCenterActiveItem();

  const handleClick = (event: React.MouseEvent, category: Category) => {
    if (category.overrideTarget && !category.disabled) {
      event.preventDefault();

      const state = createRouteState({
        considerScrollTopAnchor: true,
      });

      history.push(category.overrideTarget, state);
    }

    onClick();

    if (category.disabled) {
      showToast({
        type: 'WARNING',
        content: t(
          'Selected category does not have any products that matched your filters.',
        ),
      });
    }
  };

  return (
    <Wrapper data-testid="categoryList">
      <StyledHorizontalList ref={sliderRef}>
        {categories.map((category, index) => {
          const {active, disabled, label, target} = category;
          const datatestid = active ? '_active' : '';
          return (
            <Category
              as={disabled || useButtonLink ? 'button' : UnstyledLink}
              $isDisabled={disabled}
              key={`categories-slider-category-${label}`}
              onClick={event => handleClick(event, category)}
              ref={active ? activeCategoryRef : null}
              $selected={active}
              to={useButtonLink ? undefined : target}
              considerScrollTopAnchor
              data-testid={`categorySliderLink_${index}${datatestid}`}
            >
              {label}
            </Category>
          );
        })}
      </StyledHorizontalList>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  height: 2rem;
  width: 100%;
`;

const StyledHorizontalList = styled(HorizontalList)`
  width: 100%;
`;

const Category = styled(UnstyledLink)<{
  $isDisabled?: boolean;
  $selected?: boolean;
  to: string;
}>`
  box-sizing: border-box;
  padding: 0 0.625rem;
  height: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  border-radius: 4px;
  margin-right: 0.5rem;
  flex-shrink: 0;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: auto Highlight;
    outline: auto -webkit-focus-ring-color;
  }

  &:visited {
    color: ${COLORS.Grey1};
  }

  ${({$selected, $isDisabled}) =>
    !$selected &&
    !$isDisabled &&
    css`
      background-color: ${COLORS.Grey4}!important;
      border: 1px solid ${COLORS.Grey4}!important;
      color: ${COLORS.Grey1}!important;
    `}

  ${({$selected}) =>
    $selected &&
    css`
      background-color: ${COLORS.White}!important;
      color: ${COLORS.Black}!important;
      border: 1px solid ${COLORS.Black};
    `}

  ${({$isDisabled}) =>
    $isDisabled &&
    css`
      background-color: ${COLORS.Grey5}!important;
      color: ${COLORS.Grey3}!important;
      border: 1px solid ${COLORS.Grey5};
    `}
`;

const useCenterActiveItem = () => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const activeCategoryRef = useRef<HTMLDivElement>(null);
  useIsomorphicLayoutEffect(() => {
    if (!activeCategoryRef.current || !sliderRef.current) {
      return;
    }
    const offsetLeft = activeCategoryRef.current.offsetLeft;
    const offsetWidth = activeCategoryRef.current.offsetWidth;
    const sliderWidth = sliderRef.current.offsetWidth;
    if (offsetLeft) {
      sliderRef.current.scrollLeft =
        offsetLeft + (offsetWidth - sliderWidth) / 2;
    }
  }, [activeCategoryRef, sliderRef]);

  return {sliderRef, activeCategoryRef};
};
